import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Retrieve credentials from environment variables or custom localStorage settings
const getSupabaseConfig = () => {
  const envUrl = import.meta.env.VITE_SUPABASE_URL || '';
  const envKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

  const storedUrl = typeof window !== 'undefined' ? localStorage.getItem('narawadee_supabase_url') || '' : '';
  const storedKey = typeof window !== 'undefined' ? localStorage.getItem('narawadee_supabase_key') || '' : '';

  const url = storedUrl || envUrl;
  const anonKey = storedKey || envKey;

  const isConfigured = Boolean(
    url && 
    url.trim() !== '' && 
    !url.includes('your-project') && 
    anonKey && 
    anonKey.trim() !== '' && 
    !anonKey.includes('your-anon-key')
  );

  return { url, anonKey, isConfigured };
};

let supabaseInstance: SupabaseClient | null = null;

export const getSupabaseClient = (): SupabaseClient | null => {
  const config = getSupabaseConfig();
  if (!config.isConfigured) {
    return null;
  }
  
  if (!supabaseInstance) {
    try {
      supabaseInstance = createClient(config.url, config.anonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
        },
      });
    } catch (e) {
      console.warn('Failed to initialize Supabase client:', e);
      return null;
    }
  }
  return supabaseInstance;
};

export const saveSupabaseCredentials = (url: string, key: string) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('narawadee_supabase_url', url.trim());
    localStorage.setItem('narawadee_supabase_key', key.trim());
    supabaseInstance = null; // reset to recreate
  }
};

export const clearSupabaseCredentials = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('narawadee_supabase_url');
    localStorage.removeItem('narawadee_supabase_key');
    supabaseInstance = null;
  }
};

export const checkSupabaseConnection = async (): Promise<{ success: boolean; message: string }> => {
  const config = getSupabaseConfig();
  if (!config.isConfigured) {
    return {
      success: false,
      message: 'ยังไม่ได้ระบุ Supabase URL หรือ Anon Key (กำลังทำงานในโหมดจำลองระบบข้อมูล)',
    };
  }

  const client = getSupabaseClient();
  if (!client) {
    return { success: false, message: 'ไม่สามารถสร้างการเชื่อมต่อกับ Supabase ได้' };
  }

  // ทดสอบการเชื่อมต่อโดยลองดึงข้อมูลจาก profiles table
  try {
    const { error, count } = await client.from('profiles').select('*', { count: 'exact', head: true });
    if (error) {
      // Error อาจเกิดจาก RLSหรือ table ยังไม่มีข้อมูล แต่ connection ทำงานได้
      if (error.message.includes('relation') || error.message.includes('does not exist')) {
        return {
          success: true,
          message: 'เชื่อมต่อ Supabase สำเร็จแล้ว แต่ตารางยังไม่มีข้อมูล (โปรดรัน SQL Schema)',
        };
      }
      // Error เกี่ยวกับ column 'email' ที่เคยเกิด — ตอนนี้ควรเป็นเรื่องเก่าแล้วหลังจากรัน reset script
      return { success: false, message: `การเชื่อมต่อผิดพลาด: ${error.message}` };
    }
    return { success: true, message: `เชื่อมต่อ Supabase ฐานข้อมูลสำเร็จ! (มี ${count} รายการใน profiles)` };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    return { success: false, message: `เกิดข้อผิดพลาดในการทดสอบ: ${message}` };
  }
};

export const getStoredConfig = () => getSupabaseConfig();

export const SUPABASE_SQL_SCHEMA = `-- ==========================================================
-- สคริปต์สร้างฐานข้อมูลสำหรับ: นราวดี อิลิแกนซ์ (Narawadee Elegance)
-- คัดลอกคำสั่งนี้ไปวางใน Supabase SQL Editor แล้วกด "RUN"
-- ==========================================================

-- 1. ตารางข้อมูลโปรไฟล์ผู้ใช้งาน (Profiles)
CREATE TABLE IF NOT EXISTS public.profiles (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  email TEXT UNIQUE NOT NULL,
  full_name TEXT NOT NULL,
  role TEXT NOT NULL CHECK (role IN ('resident', 'staff', 'admin')),
  house_number TEXT,
  phone TEXT,
  department TEXT,
  avatar_url TEXT,
  created_at TIMESTAMPTZ DEFAULT timezone('utc'::text, now()) NOT NULL
);

-- 2. ตารางข่าวสารและประกาศโครงการ (Announcements)
CREATE TABLE IF NOT EXISTS public.announcements (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  title TEXT NOT NULL,
  content TEXT NOT NULL,
  category TEXT NOT NULL CHECK (category IN ('general', 'maintenance', 'event', 'urgent')),
  is_pinned BOOLEAN DEFAULT false,
  author_name TEXT NOT NULL,
  image_url TEXT,
  created_at TIMESTAMPTZ DEFAULT timezone('utc'::text, now()) NOT NULL
);

-- 3. ตารางพัสดุและไปรษณีย์ (Parcels)
CREATE TABLE IF NOT EXISTS public.parcels (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  tracking_number TEXT NOT NULL,
  courier_name TEXT NOT NULL,
  house_number TEXT NOT NULL,
  recipient_name TEXT NOT NULL,
  status TEXT NOT NULL DEFAULT 'waiting' CHECK (status IN ('waiting', 'picked_up')),
  received_at TIMESTAMPTZ DEFAULT timezone('utc'::text, now()) NOT NULL,
  picked_up_at TIMESTAMPTZ,
  image_url TEXT,
  note TEXT
);

-- 4. ตารางแจ้งซ่อมและข้อร้องเรียน (Maintenance Tickets)
CREATE TABLE IF NOT EXISTS public.maintenance_tickets (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  house_number TEXT NOT NULL,
  reporter_name TEXT NOT NULL,
  title TEXT NOT NULL,
  category TEXT NOT NULL CHECK (category IN ('water', 'electricity', 'road', 'security', 'landscape', 'other')),
  description TEXT NOT NULL,
  status TEXT NOT NULL DEFAULT 'pending' CHECK (status IN ('pending', 'in_progress', 'completed', 'cancelled')),
  priority TEXT NOT NULL DEFAULT 'medium' CHECK (priority IN ('low', 'medium', 'high', 'urgent')),
  image_url TEXT,
  staff_notes TEXT,
  assigned_to TEXT,
  created_at TIMESTAMPTZ DEFAULT timezone('utc'::text, now()) NOT NULL,
  updated_at TIMESTAMPTZ DEFAULT timezone('utc'::text, now()) NOT NULL
);

-- 5. ตารางค่าส่วนกลางและบิล (Common Fee Bills)
CREATE TABLE IF NOT EXISTS public.bills (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  house_number TEXT NOT NULL,
  resident_name TEXT NOT NULL,
  period TEXT NOT NULL,
  amount NUMERIC NOT NULL,
  due_date TEXT NOT NULL,
  status TEXT NOT NULL DEFAULT 'unpaid' CHECK (status IN ('unpaid', 'pending_verification', 'paid', 'overdue')),
  slip_url TEXT,
  paid_at TIMESTAMPTZ,
  payment_method TEXT,
  verified_by TEXT
);

-- 6. ตารางบัตรผ่านผู้มาติดต่อ (Visitor Passes)
CREATE TABLE IF NOT EXISTS public.visitors (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  pass_code TEXT NOT NULL,
  host_house_number TEXT NOT NULL,
  host_name TEXT NOT NULL,
  visitor_name TEXT NOT NULL,
  license_plate TEXT NOT NULL,
  visit_date TEXT NOT NULL,
  purpose TEXT NOT NULL,
  status TEXT NOT NULL DEFAULT 'approved' CHECK (status IN ('approved', 'checked_in', 'checked_out', 'cancelled')),
  entry_time TIMESTAMPTZ,
  exit_time TIMESTAMPTZ,
  created_at TIMESTAMPTZ DEFAULT timezone('utc'::text, now()) NOT NULL
);

-- 7. ตารางการจองพื้นที่ส่วนกลาง (Facility Bookings)
CREATE TABLE IF NOT EXISTS public.facility_bookings (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  facility_id TEXT NOT NULL,
  facility_name TEXT NOT NULL,
  house_number TEXT NOT NULL,
  resident_name TEXT NOT NULL,
  booking_date TEXT NOT NULL,
  time_slot TEXT NOT NULL,
  status TEXT NOT NULL DEFAULT 'confirmed' CHECK (status IN ('confirmed', 'cancelled')),
  created_at TIMESTAMPTZ DEFAULT timezone('utc'::text, now()) NOT NULL
);

-- 8. ตารางลงเวลาเข้า-ออกงานของพนักงาน (Staff Attendance Records)
CREATE TABLE IF NOT EXISTS public.attendance_records (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id TEXT NOT NULL,
  staff_name TEXT NOT NULL,
  department TEXT NOT NULL,
  role_title TEXT NOT NULL,
  date TEXT NOT NULL,
  shift_name TEXT NOT NULL,
  shift_time TEXT NOT NULL,
  clock_in_time TEXT NOT NULL,
  clock_in_photo TEXT,
  clock_in_location TEXT,
  clock_in_status TEXT NOT NULL CHECK (clock_in_status IN ('on_time', 'late')),
  clock_out_time TEXT,
  clock_out_photo TEXT,
  clock_out_location TEXT,
  clock_out_status TEXT CHECK (clock_out_status IN ('normal', 'ot', 'early_leave')),
  work_duration_hours NUMERIC,
  notes TEXT,
  created_at TIMESTAMPTZ DEFAULT timezone('utc'::text, now()) NOT NULL
);

-- 9. ตารางบันทึกการสแกนตรวจจุดลาดตระเวน รปภ. (Security Patrol Scan Logs)
CREATE TABLE IF NOT EXISTS public.patrol_scan_logs (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  checkpoint_id TEXT NOT NULL,
  checkpoint_code TEXT NOT NULL,
  checkpoint_name TEXT NOT NULL,
  checkpoint_zone TEXT NOT NULL,
  officer_id TEXT NOT NULL,
  officer_name TEXT NOT NULL,
  round_number INT NOT NULL DEFAULT 1,
  round_time_slot TEXT NOT NULL,
  scanned_at TIMESTAMPTZ DEFAULT timezone('utc'::text, now()) NOT NULL,
  scanned_time TEXT NOT NULL,
  status TEXT NOT NULL CHECK (status IN ('normal', 'abnormal')),
  checklist_results JSONB DEFAULT '{}'::jsonb,
  note TEXT,
  photo_url TEXT,
  location_verified BOOLEAN DEFAULT true
);

-- เปิดใช้งาน Row Level Security (RLS) แบบสาธารณะสำหรับ Web App
ALTER TABLE public.profiles ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.announcements ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.parcels ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.maintenance_tickets ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.bills ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.visitors ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.facility_bookings ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.attendance_records ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.patrol_scan_logs ENABLE ROW LEVEL SECURITY;

-- สร้างนโยบายการเข้าถึงข้อมูล (Policies) เพื่อให้ Web App สามารถอ่าน-เขียนได้
CREATE POLICY "Allow public read-write for profiles" ON public.profiles FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow public read-write for announcements" ON public.announcements FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow public read-write for parcels" ON public.parcels FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow public read-write for maintenance" ON public.maintenance_tickets FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow public read-write for bills" ON public.bills FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow public read-write for visitors" ON public.visitors FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow public read-write for bookings" ON public.facility_bookings FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow public read-write for attendance" ON public.attendance_records FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow public read-write for patrol" ON public.patrol_scan_logs FOR ALL USING (true) WITH CHECK (true);
`;
